import type { Json } from "../../lib/database.types";

/**
 * Site looks; each id is a `data-site-theme` value styled in src/index.css.
 * Accents are the colours offered with that theme, default first; each id is a
 * `data-site-accent` value. Every accent reads clearly on its theme.
 */
export const SITE_THEMES = [
  {
    id: "warm",
    name: "Warm",
    description: "Soft cream, terracotta and an expressive serif.",
    accents: [
      { id: "terracotta", name: "Terracotta" },
      { id: "sage", name: "Sage" },
      { id: "ink", name: "Ink" },
    ],
  },
  {
    id: "gallery",
    name: "Gallery",
    description: "Crisp white walls and tight, modern type.",
    accents: [
      { id: "black", name: "Black" },
      { id: "terracotta", name: "Terracotta" },
      { id: "sage", name: "Sage" },
    ],
  },
  {
    id: "night",
    name: "Night",
    description: "Dark and calm, with gold accents.",
    accents: [
      { id: "gold", name: "Gold" },
      { id: "clay", name: "Clay" },
      { id: "sage", name: "Sage" },
      { id: "terracotta", name: "Terracotta" },
    ],
  },
] as const;

export type SiteThemeId = (typeof SITE_THEMES)[number]["id"];
export type SiteAccentId =
  (typeof SITE_THEMES)[number]["accents"][number]["id"];

export const DEFAULT_SITE_THEME: SiteThemeId = "warm";

/** The theme each kind of starter site opens on: the one that shows it off. */
export const STARTER_THEMES: Record<string, SiteThemeId> = {
  portfolio: "warm",
  photography: "gallery",
  apps: "gallery",
  video: "night",
  personal: "warm",
};

export const LOGO_SIZES = ["small", "medium", "large"] as const;
export type LogoSize = (typeof LOGO_SIZES)[number];
/** Each logo size's height in the header on larger screens, in px. */
export const LOGO_HEIGHTS: Record<LogoSize, number> = { small: 28, medium: 36, large: 48 };
/** The widest a logo shows in the header, in px. */
export const LOGO_MAX_WIDTH = 240;
/** The slider's steps for how round the site's buttons are, square to pill. */
export const BUTTON_RADII = ["0", "4", "8", "12", "16", "20", "pill"] as const;
export type ButtonRadius = (typeof BUTTON_RADII)[number];
/** What the logo file is like, read on upload, for advice on how it'll look. */
export type LogoInfo = { width: number; height: number; vector: boolean; solid: boolean };
/** Whether the header shows the logo alone, or the logo and the site's name. */
export type LogoDisplay = "logo" | "both";
/** How a logo reads: mostly dark or mostly light marks (checked on upload). */
export type LogoTone = "dark" | "light" | "mixed";
/** Themes with a dark background, where a dark logo disappears. */
export const DARK_THEMES: readonly SiteThemeId[] = ["night"];

/** The site-wide look, as stored in the draft's `settings`. */
export type SiteDesign = {
  theme: SiteThemeId;
  accent: SiteAccentId;
  /** Asset id of the uploaded site icon; null uses the name's first letter. */
  icon: string | null;
  /** Asset id of the header logo; null shows the site's name. */
  logo: string | null;
  logoDisplay: LogoDisplay;
  logoSize: LogoSize;
  logoTone: LogoTone | null;
  logoInfo: LogoInfo | null;
  /** How round every button is; null keeps the theme's own. */
  buttonRadius: ButtonRadius | null;
};

export const siteThemeById = (id: SiteThemeId) =>
  SITE_THEMES.find((theme) => theme.id === id)!;

const settingsObject = (settings: Json) =>
  settings && typeof settings === "object" && !Array.isArray(settings)
    ? settings
    : {};

const logoInfoFrom = (value: Json | undefined): LogoInfo | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const { width, height, vector, solid } = value;
  return typeof width === "number" && typeof height === "number" && width > 0 && height > 0
    ? { width, height, vector: vector === true, solid: solid === true }
    : null;
};

export const siteDesignFrom = (settings: Json): SiteDesign => {
  const stored = settingsObject(settings);
  const theme =
    SITE_THEMES.find((item) => item.id === stored.theme) ??
    siteThemeById(DEFAULT_SITE_THEME);
  // A saved accent the theme doesn't offer falls back to its default, and
  // comes back if the site returns to a theme that has it.
  const accent =
    theme.accents.find((item) => item.id === stored.accent) ?? theme.accents[0];
  return {
    theme: theme.id,
    accent: accent.id,
    icon: typeof stored.icon === "string" ? stored.icon : null,
    logo: typeof stored.logo === "string" ? stored.logo : null,
    logoDisplay: stored.logoDisplay === "both" ? "both" : "logo",
    logoSize: LOGO_SIZES.find((size) => size === stored.logoSize) ?? "medium",
    logoTone: (["dark", "light", "mixed"] as const).find((tone) => tone === stored.logoTone) ?? null,
    logoInfo: logoInfoFrom(stored.logoInfo),
    buttonRadius: BUTTON_RADII.find((radius) => radius === stored.buttonRadius) ?? null,
  };
};

export const withSiteDesign = (
  settings: Json,
  patch: Partial<SiteDesign>,
): Json => ({ ...settingsObject(settings), ...patch });

/**
 * Attributes that apply a design to an element. The theme's default accent
 * sets no attribute, so the theme's hand-tuned tints stay in place.
 */
export const siteDesignAttributes = (
  design: Pick<SiteDesign, "theme" | "accent"> & Partial<Pick<SiteDesign, "buttonRadius">>,
) => ({
  "data-button-radius": design.buttonRadius ?? undefined,
  "data-site-theme": design.theme,
  "data-site-accent":
    design.accent === siteThemeById(design.theme).accents[0].id
      ? undefined
      : design.accent,
});
